//CRN_20251218 - ⚠️ ngOnInit() called for components/directives & not services ⚠️

import { CommunicationCompositionDTO } from 'src/app/core/models/system/communication/communication-composition.dto';
import { CommunicationCompositionResultDTO } from 'src/app/core/models/system/communication/communication-composition-result.dto';
import { CommunicationDeliveryResultDTO } from 'src/app/core/models/system/communication/communication-delivery-result.dto';
import { CommunicationDTO } from 'src/app/core/models/system/communication/communication.dto';
import { ConfigSystemService } from '../config-system/config-system-service';
import { environment } from 'src/environments/environment';
import { ConfigSystemParams, Duration, eSimApiPaths } from 'src/app/core/consts';
import { EncryptionService } from '../../general/encryption-service';
import { ExceptionsService } from '../../general/exceptions-service';
import { forkJoin, interval, Observable, of, startWith, switchMap } from 'rxjs';
import { HttpClientService } from '../../general/http-client-service';
import { Injectable } from '@angular/core';
import { StringResultDTO } from 'src/app/core/interfaces/general/string-result';

@Injectable({ providedIn: 'root' })

export class CommunicationService {
  
  constructor(
    private configSystemService: ConfigSystemService,
    private encryptionService  : EncryptionService,
    private exceptionsService  : ExceptionsService,
    private httpClientService  : HttpClientService
  ) { }
    
  public compose(composition: CommunicationCompositionDTO): Observable<CommunicationCompositionResultDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(JSON.stringify(composition), true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const objectEncSS=encResult.result;

                            return this.httpClientService.post(environment.eSimApi
                                                               + eSimApiPaths.Communications
                                                               + "/"
                                                               + eSimApiPaths.CommunicationsCompose,
                                                               { objectEncSS: objectEncSS });
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public delete(id: number): Observable<CommunicationDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>id, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const idEncSS=encResult.result;

                            return this.httpClientService.delete(environment.eSimApi
                                                                 + eSimApiPaths.Communications
                                                                 + "?idEncSS=" + idEncSS, undefined);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getDeliveryResults(communicationId: number): Observable<CommunicationDeliveryResultDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>communicationId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const communicationIdEncSS=encodeURIComponent(encResult.result);

                            return this.httpClientService.get<CommunicationDeliveryResultDTO[]>(
                              environment.eSimApi
                              + eSimApiPaths.Communications
                              + "/"
                              + eSimApiPaths.CommunicationsDeliveryResults
                              + "/"
                              + communicationIdEncSS, undefined);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getForLoggedInUser(userId: number): Observable<CommunicationDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const userIdEncSS=encResult.result;
                            
                            return this.httpClientService.get<CommunicationDTO[]>(
                              environment.eSimApi
                              + eSimApiPaths.Communications
                              + "/"
                              + eSimApiPaths.CommunicationsForUser
                              + "?userIdEncSS=" + userIdEncSS, undefined); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getForLoggedInUserActiveOnly(userId: number): Observable<CommunicationDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const userIdEncSS=encResult.result;
                            
                            return this.httpClientService.get<CommunicationDTO[]>(
                              environment.eSimApi
                              + eSimApiPaths.Communications
                              + "/"
                              + eSimApiPaths.CommunicationsForUserActiveOnly
                              + "?userIdEncSS=" + userIdEncSS, undefined); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
        
  public getForLoggedInUserActiveOnlyUpdates(userId: number): Observable<CommunicationDTO[]> {
    try {
      return interval(this.configSystemService.getValueAsInteger(ConfigSystemParams.PollObservableNotificationsMilliSecs, Duration._1MinMilli))
             .pipe(startWith(0),
                   switchMap(() => this.getForLoggedInUserActiveOnly(userId))
             )
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getById(id: number): Observable<CommunicationDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>id, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const idEncSS=encResult.result;
                            
                            return this.httpClientService.getById(environment.eSimApi
                                                                  + eSimApiPaths.Communications
                                                                  + "?idEncSS=" + idEncSS, undefined); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public markAsRead(loggedInUserId: number, userId: number, communicationId: number): Observable<CommunicationDTO> {
    try {
      return forkJoin({
               loggedInUserIdEncResult: this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>loggedInUserId),
               userIdEncResult        : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId, true)
             })
             .pipe(
               switchMap(({ loggedInUserIdEncResult, userIdEncResult }) => {
                 const loggedInUserIdEnc=loggedInUserIdEncResult.result;
                 const userIdEncSS      =userIdEncResult.result;
                 
                 return this.httpClientService.post(environment.eSimApi
                                                    + eSimApiPaths.Communications
                                                    + "/"
                                                    + eSimApiPaths.CommunicationsMarkAsRead
                                                    + "?loggedInUserIdEnc=" + loggedInUserIdEnc
                                                    + "&userIdEncSS="       + userIdEncSS
                                                    + "&communicationId="   + communicationId,
                                                    {}
                                                   );
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public markAsUnread(userId: number, communicationId: number): Observable<CommunicationDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const userIdEncSS=encResult.result;
                            
                            return this.httpClientService.post(environment.eSimApi
                                                               + eSimApiPaths.Communications
                                                               + "/"
                                                               + eSimApiPaths.CommunicationsMarkAsUnread
                                                               + "?userIdEncSS=" + userIdEncSS
                                                               + "&communicationId=" + communicationId, {}); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public post(communication: CommunicationDTO): Observable<CommunicationDTO> {
    try {
      //JSON.stringify(communication);
      
      return this.encryptionService.encrypt_AesServerSideForWeb(JSON.stringify(communication), true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const objectEncSS=encResult.result;
                            
                            return this.httpClientService.post(environment.eSimApi
                                                               + eSimApiPaths.Communications
                                                               + "?objectEncSS=" + objectEncSS, {}); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public put(communication: CommunicationDTO): Observable<CommunicationDTO> {
    try {
      //JSON.stringify(communication);
      
      return this.encryptionService.encrypt_AesServerSideForWeb(JSON.stringify(communication), true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const objectEncSS=encResult.result;
                            
                            return this.httpClientService.put(environment.eSimApi
                                                              + eSimApiPaths.Communications
                                                              + "?id=" + communication.id
                                                              + "&objectEncSS=" + objectEncSS, undefined, {}); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public save(communication: CommunicationDTO): Observable<any> {
    try {
      //JSON.stringify(communication);
      
      if (communication.id && <number>communication.id>0)
        return this.put(communication);
  
      return this.post(communication);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
}
